import { useCallback, useMemo } from 'react';

import ReferralForm from 'components/modals/referral/form/ReferralForm';
import { referralFormValidation } from 'components/modals/referral/form/referralFormValidation';
import { ActivityInputValues } from 'components/shared/activityInput/useActivityInput';
import StandardModal, { PatientName } from 'components/shared/StandardModal';
import { FormProvider } from 'context/form';
import { FormHelpers, FormMeta, FormValues } from 'context/form/types';
import useInvalidateQueries from 'hooks/useInvalidateQueries';
import useMutateWithToast from 'hooks/useMutateWithToast';
import { extractDigits } from 'lib/phone';
import Episode from 'models/Episode';
import Group from 'models/Group';
import GroupType from 'models/GroupType';
import LocationEpisode from 'models/LocationEpisode';
import { ReviewType } from 'models/Review';
import { createReview } from 'services/api/reviews';
import { useToastActions } from 'stores/toastStore';

import { buildReferralFormSteps, ReferralSteps, USState } from './form/helpers';

export type SelectValue = { label: string; value: string };
export const emptyValue: SelectValue = { label: '', value: '' };

export type ReferToFormValues = {
  note: ActivityInputValues;
  providerType: GroupType;
  group: Group;
};

export type ReferralDeterminantsFormValues = {
  expectedDischargeDate?: Date;
  address1?: string;
  address2?: string;
  city?: string;
  state?: USState;
  zip?: string;
  phone?: string;
};

export type ReferralFormValues = Partial<ReferToFormValues & ReferralDeterminantsFormValues>;

export type ReferralFormMeta = {
  currentStep: ReferralSteps;
  isSubmitting: boolean;
  isUploadingAttachments: boolean;
  submitText: string;
  steps: ReferralSteps[];
  onCancel: () => void;
  onPreviousStep: (setMeta: (key: string, value: any) => void, currentStep: ReferralSteps) => void;
  onNextStep: (setMeta: (key: string, value: any) => void, currentStep: ReferralSteps) => void;
};

type SendReferralModalProps = {
  onCancel: () => void;
  onSuccess: () => void;
  locationEpisode: LocationEpisode;
  patientName: string;
  episode: Episode;
};

export default function SendReferralModal(props: SendReferralModalProps) {
  const { onCancel, onSuccess, patientName, locationEpisode, episode } = props;
  const { addToast } = useToastActions();

  const invalidateQueries = useInvalidateQueries();

  const { mutate } = useMutateWithToast({
    mutationFn: createReview,
    onSuccess: () => {
      invalidateQueries({
        locationEpisode: { id: locationEpisode.id },
        referrals: true,
        attachmentsCount: true,
      });
      onSuccess();
    },
  });

  const latestReferralData = locationEpisode.latestReferralReview?.data;
  const importedPatientValues = useMemo(
    () => ({ address: episode.patient.address, phoneNumber: episode.patient.phoneNumber }),
    [episode.patient.address, episode.patient.phoneNumber]
  );

  const initialValues = useMemo(() => {
    return {
      address1: latestReferralData?.address1 ?? importedPatientValues?.address?.address1,
      address2: latestReferralData?.address2 ?? importedPatientValues?.address?.address2,
      city: latestReferralData?.city ?? importedPatientValues?.address?.city,
      state: latestReferralData?.state ?? importedPatientValues?.address?.state,
      zip: latestReferralData?.zip ?? importedPatientValues?.address?.zip,
      phone: '',
    };
  }, [latestReferralData, importedPatientValues]);

  const handleSubmit = async (
    values: FormValues<ReferralFormValues>,
    _meta: FormMeta<ReferralFormMeta>,
    helpers: FormHelpers<ReferralFormValues, ReferralFormMeta>
  ) => {
    helpers.setMeta('isSubmitting', true);

    delete values.note?.escalationType;

    mutate(
      {
        locationEpisodeId: locationEpisode.id,
        groupId: values.group?.id ?? '',
        type: ReviewType.REFERRAL,
        note: values.note
          ? {
              ...values.note,
              attachments: values.note.attachments?.map((upload) => upload.serialize()),
            }
          : undefined,
        expectedDischargeDate: values.expectedDischargeDate,
        address1: values.address1,
        address2: values.address2,
        city: values.city,
        state: values.state,
        zip: values.zip,
        phoneNumber: extractDigits(values.phone || '') || undefined,
      },
      {
        onError: () => {
          addToast({ text: 'Something went wrong. Please try again' });
        },
        onSettled: () => {
          helpers.setMeta('isSubmitting', false);
        },
      }
    );
  };

  const steps = useMemo(() => buildReferralFormSteps(), []);

  const onPreviousStep = useCallback(
    (setMeta: (key: string, value: any) => void, currentStep: ReferralSteps) => {
      if (currentStep === ReferralSteps.ReferTo) {
        onCancel();
        return;
      }

      const currentIndex = steps.indexOf(currentStep);
      setMeta('currentStep', steps[currentIndex - 1]);
    },
    [steps, onCancel]
  );

  const onNextStep = useCallback(
    (setMeta: (key: string, value: any) => void, currentStep: ReferralSteps) => {
      const currentIndex = steps.indexOf(currentStep);

      setMeta('currentStep', steps[currentIndex + 1]);
    },
    [steps]
  );

  return (
    <StandardModal title='Refer Patient' onCancel={onCancel}>
      <PatientName>Patient: {patientName}</PatientName>
      <FormProvider<ReferralFormValues, ReferralFormMeta>
        onSubmit={handleSubmit}
        yupSchema={referralFormValidation()}
        initialValues={initialValues as any}
        initialMeta={{
          currentStep: ReferralSteps.ReferTo,
          steps,
          submitText: 'Submit Referral',
          onCancel,
          onNextStep,
          onPreviousStep,
        }}
        resetOnInitialValuesChange>
        <ReferralForm
          locationEpisode={locationEpisode}
          initialPhoneNumber={latestReferralData?.phoneNumber ?? importedPatientValues?.phoneNumber}
        />
      </FormProvider>
    </StandardModal>
  );
}
